'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';

import { t } from '../../../../i18n';
import Button from '../../../../components/Button/Button';
import Icon from '../../../../components/Icon';
import ListZeroState from '../../../../components/ListZeroState';
import {
    useHasRightColumn,
    useReportComments,
} from '../ReportCommentsProvider';
import { useBottomSheet } from '../BottomSheet';
import { commentsMarkedAt, scrollAbove } from '../marks';
import Byline from './Byline';
import DeleteComment from './DeleteComment';
import DraftComposer from './DraftComposer';
import Excerpt from './Excerpt';

import s from './CommentPanel.module.scss';

// Where the open panel is: in place of the right column from 1080px, and in a
// bottom sheet below, where there is no right column. Null while it is closed.
export const useCommentPanelLayout = () => {
    const { comments, isPanelOpen } = useReportComments();
    const hasRightColumn = useHasRightColumn();
    if (!comments || !isPanelOpen) return null;
    return hasRightColumn ? 'column' : 'sheet';
};

// Not compareBoundaryPoints, which throws on a range whose text node was
// replaced in the moment before the comments are placed again.
const compareStarts = (a, b) => {
    if (a.startContainer === b.startContainer)
        return a.startOffset - b.startOffset;
    return a.startContainer.compareDocumentPosition(b.startContainer) &
        Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1;
};

// In the order their text comes in the report, the way Word lists comments.
// Orphaned comments have no place in it and come last. Comments on the same
// text keep the order the API gives, newest first.
const inReportOrder = (comments, placedRanges) =>
    [...comments].sort((a, b) => {
        const rangeA = placedRanges.get(a.id);
        const rangeB = placedRanges.get(b.id);
        if (!rangeA || !rangeB) return Boolean(rangeB) - Boolean(rangeA);
        return compareStarts(rangeA, rangeB);
    });

// A placed comment goes to its mark when it is clicked anywhere. An orphaned
// comment's text is no longer in the report, so it has nowhere to go.
const Comment = ({
    comment,
    isActive,
    isFaded,
    isPlaced,
    isExpanded,
    onShow,
    onToggle,
    onDeleted,
}) => {
    return (
        <li
            className={classNames(s.Comment, {
                [s['Comment--Placed']]: isPlaced,
                [s['Comment--Orphaned']]: !isPlaced,
                [s['Comment--Active']]: isActive,
                [s['Comment--Faded']]: isFaded,
            })}
            aria-current={isActive || undefined}>
            <Byline comment={comment} />
            {!isPlaced && (
                <span className={s.Orphaned}>
                    {t('reportComments.orphaned')}
                </span>
            )}
            <blockquote className={s.Quote}>
                {isPlaced ? (
                    <button type="button" className={s.Show} onClick={onShow}>
                        <Excerpt isExpanded={isExpanded} onToggle={onToggle}>
                            {comment.selected_text_display}
                        </Excerpt>
                    </button>
                ) : (
                    <Excerpt isExpanded={isExpanded} onToggle={onToggle}>
                        {comment.selected_text_display}
                    </Excerpt>
                )}
            </blockquote>
            <div className={s.Body}>
                <p className={s.BodyLabel}>
                    {t('reportComments.commentHeading')}
                </p>
                <p className={s.Text}>
                    <Excerpt
                        isExpanded={isExpanded}
                        onToggle={() => {
                            if (!getSelection().isCollapsed) return;
                            if (isPlaced && !isActive) onShow();
                            else onToggle();
                        }}>
                        {comment.comment}
                    </Excerpt>
                </p>
            </div>
            {comment.can_delete && (
                <DeleteComment id={comment.id} onDeleted={onDeleted} />
            )}
        </li>
    );
};

const CommentPanel = () => {
    const titleId = useId();
    const titleRef = useRef(null);
    const listRef = useRef(null);
    const {
        comments,
        isPanelCollapsed,
        toggleCollapsed,
        closePanel,
        draft,
        activeCommentIds,
        placedRanges,
        draftRange,
        showComment,
    } = useReportComments();
    const sheet = useBottomSheet();
    const [expandedId, setExpandedId] = useState(null);

    // Escape and the close button close the panel. Clicking elsewhere in the
    // report leaves the right column open, so readers can move between text and
    // comments, but closes the bottom sheet. A modal dialog on top, such as the
    // fullscreen PDF viewer, takes Escape for itself.
    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key !== 'Escape') return;
            if (document.querySelector('[aria-modal="true"]')) return;
            closePanel();
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [closePanel]);

    useEffect(() => {
        if (!sheet || draft) return;
        const closeOnOutsideClick = (event) => {
            if (sheet.contains(event.target)) return;
            if (event.target.closest('a, button, [aria-modal="true"]')) return;
            if (!document.getSelection()?.isCollapsed) return;
            if (
                commentsMarkedAt(placedRanges, event.clientX, event.clientY)
                    .length
            )
                return;
            closePanel();
        };
        document.addEventListener('click', closeOnOutsideClick);
        return () => document.removeEventListener('click', closeOnOutsideClick);
    }, [sheet, draft, placedRanges, closePanel]);

    // Brings the first of the active comments into view, such as those on a
    // clicked mark. The bottom sheet has room for little more than one, so it
    // goes to the top there, and is there at once when the chevron brings back
    // the rest around it.
    useEffect(() => {
        listRef.current?.querySelector('[aria-current]')?.scrollIntoView({
            block: sheet ? 'start' : 'nearest',
            behavior: sheet ? 'instant' : 'smooth',
        });
    }, [activeCommentIds, sheet, isPanelCollapsed]);

    // The bottom sheet covers the lower part of the report. Text shown while
    // it is open is brought up above it.
    const scrollAboveSheet = useCallback(
        (range) => scrollAbove(range, window.innerHeight - sheet.offsetHeight),
        [sheet]
    );

    // The text it opens on, a tapped mark's or a new draft's. Not on a change
    // of active comment: one shown from the sheet scrolls by itself, once its
    // accordion has opened.
    const shownRange = draftRange ?? placedRanges.get(activeCommentIds[0]);
    useEffect(() => {
        if (!sheet || !shownRange) return;
        scrollAboveSheet(shownRange);
    }, [sheet, draftRange]);

    // A deleted comment takes the focused "Yes, delete" with it. Focus stays
    // in the panel rather than falling back to the top of the page.
    const keepFocusInPanel = useCallback(() => titleRef.current.focus(), []);

    // What the reader is on stands out from the rest.
    const hasFocus = Boolean(draft) || activeCommentIds.length > 0;

    // The bottom sheet can shrink to what the reader is on: the active
    // comments, or else the draft. The chevron brings back the rest.
    const isCollapsed = Boolean(sheet) && hasFocus && isPanelCollapsed;
    const hasActive = activeCommentIds.length > 0;
    const ordered = inReportOrder(comments, placedRanges);
    const shownComments = isCollapsed
        ? ordered.filter((comment) => activeCommentIds.includes(comment.id))
        : ordered;
    const showsDraft = Boolean(draft) && !(isCollapsed && hasActive);

    return (
        <section className={s.Root} aria-labelledby={titleId}>
            <div className={s.Header}>
                <h2
                    ref={titleRef}
                    id={titleId}
                    className={s.Title}
                    tabIndex={-1}>
                    {t('reportComments.panelTitle')}
                </h2>
                <div className={s.Actions}>
                    {sheet && hasFocus && (
                        <button
                            type="button"
                            className={s.Toggle}
                            aria-label={t('reportComments.allComments')}
                            aria-expanded={!isCollapsed}
                            onClick={toggleCollapsed}>
                            <Icon
                                name="SelectChevron"
                                className={classNames(s.Chevron, {
                                    [s['Chevron--Up']]: isCollapsed,
                                })}
                            />
                        </button>
                    )}
                    <Button
                        modifier="Secondary"
                        size="Small"
                        title={t('reportComments.closeButton')}
                        aria-label={t('reportComments.close')}
                        onClick={closePanel}
                    />
                </div>
            </div>
            {/* Such as once the last comment is deleted. */}
            {!draft && comments.length === 0 ? (
                <ListZeroState
                    icon="AddComment"
                    title={t('reportComments.emptyTitle')}
                    text={t('reportComments.emptyText')}
                />
            ) : (
                <div className={s.Comments}>
                    {showsDraft && <DraftComposer />}
                    <ol className={s.List} ref={listRef}>
                        {shownComments.map((comment) => {
                            const isActive = activeCommentIds.includes(
                                comment.id
                            );
                            const isPlaced = placedRanges.has(comment.id);
                            return (
                                <Comment
                                    key={comment.id}
                                    comment={comment}
                                    isActive={isActive}
                                    isFaded={hasFocus && !isActive}
                                    isPlaced={isPlaced}
                                    isExpanded={expandedId === comment.id}
                                    onToggle={() => {
                                        if (isPlaced && !isActive) return;
                                        setExpandedId(
                                            expandedId === comment.id
                                                ? null
                                                : comment.id
                                        );
                                    }}
                                    onShow={() => {
                                        if (!isActive) setExpandedId(null);
                                        showComment(
                                            comment.id,
                                            sheet ? scrollAboveSheet : undefined
                                        );
                                    }}
                                    onDeleted={keepFocusInPanel}
                                />
                            );
                        })}
                    </ol>
                </div>
            )}
        </section>
    );
};

export default CommentPanel;
